import Quagga from "@ericblade/quagga2";
import React, { useCallback, useLayoutEffect, useRef } from "react";

function isMobile() {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

const defaultConstraints = {
  width: window.innerWidth > 640 ? 640 : window.innerWidth,
  height: window.innerHeight / 1.5,
  facingMode: isMobile() ? { ideal: "environment" } : { ideal: "user" },
};

const Scanner = ({
  onDetected,
  onScannerReady,
  cameraId,
  constraints = defaultConstraints,
  locator = { patchSize: "large", halfSample: false },
  numOfWorkers = navigator.hardwareConcurrency || 0,
  decoders = ["upc_reader", "ean_reader"],
  locate = true,
}) => {
  const scannerRef = useRef(null);

  const errorCheck = useCallback(
    (result) => {
      if (!onDetected) return;
      const code = result.codeResult.code;
      if (code) {
        onDetected(code);
      }
    },
    [onDetected]
  );

  useLayoutEffect(() => {
    if (!scannerRef.current) {
      console.error("Scanner reference is not set");
      return;
    }

    const cameraConstraints = {
      ...constraints,
      ...(cameraId ? { deviceId: cameraId } : {}),
    };

    Quagga.init(
      {
        inputStream: {
          type: "LiveStream",
          constraints: cameraConstraints,
          target: scannerRef.current,
        },
        locator,
        numOfWorkers,
        decoder: { readers: decoders },
        locate,
      },
      (err) => {
        if (err) {
          console.error("Error starting Quagga:", err);
          alert("Please enable the camera to scan the product: " + err.message);
          return;
        }
        Quagga.onProcessed(() => {}); // No more drawing the red box
        Quagga.onDetected(errorCheck);
        Quagga.start();
        onScannerReady?.();
      }
    );

    // Cleanup function
    return () => {
      Quagga.offDetected(errorCheck);
      Quagga.offProcessed(() => {});
      try {
        Quagga.stop();
      } catch (e) {
        console.error("Error stopping Quagga:", e);
      }
    };
  }, [
    cameraId,
    constraints,
    decoders,
    errorCheck,
    locator,
    locate,
    numOfWorkers,
    onDetected,
    onScannerReady,
  ]);

  // Define the overlay style
  const overlayStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
  };

  const holeStyle = {
    position: "absolute",
    top: "20%",
    left: "15%",
    width: "70%",
    height: "30%",
    borderRadius: "20px",
    boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
    pointerEvents: "none",
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div ref={scannerRef} style={{ width: "100%", height: "100%" }} />
      <div style={overlayStyle}>
        <div style={holeStyle}></div>
      </div>
    </div>
  );
};

export default Scanner;
